/* eslint-disable jsx-a11y/media-has-caption */
import { MDBBtn, MDBContainer } from "mdb-react-ui-kit";
import React, { FC, useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
    ETypePackage,
    TourType,
    ETypeToastMessage,
} from "@/models/common/enums";
import { checkRating } from "@/utils/common";
import { Icon } from "@iconify/react";
import playIcon from "@iconify-icons/fa-solid/play";
import pauseIcon from "@iconify-icons/fa-solid/pause";
import volumeMuteIcon from "@iconify-icons/fa-solid/volume-mute";
import volumeUpIcon from "@iconify-icons/fa-solid/volume-up";
import expandIcon from "@iconify-icons/fa-solid/expand";
import compressIcon from "@iconify-icons/fa-solid/compress";
import { starsArray } from "@/utils/star-utils";
import { useRouter } from "next/router";
import { useStore } from "@/store/index";
import { useToggleFavourite } from "@/utils/queries/hooks/useToggleFavourite";
import { useIsMD } from "../../utils/hooks/useWindowSizes";
import HeadBox from "./HeadBox";
import mediaControlSvg from "../../utils/svg-icon/mediaControl";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";
import { TourReadableTO } from "../../models/detail";
import styles from "../../styles/detail/DetailHead.module.scss";
import { URLS } from "../../utils/constants";

const PackageDetailHead: FC<{
    isVideoAvailable?: boolean;
    packageDetail: TourReadableTO;
    days: string;
    isPremium: boolean;
    isPromo: boolean;
    setIsPremium: (el: boolean) => void;
    typePackage: ETypePackage;
    showAccomodations?: boolean;
    // eslint-disable-next-line sonarjs/cognitive-complexity
}> = ({
    packageDetail,
    days,
    isPremium,
    isPromo,
    setIsPremium,
    typePackage,
    showAccomodations = true,
    isVideoAvailable = true,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const [videoPlay, setVideoPlay] = useState<boolean>(false);
    const [error, setError] = useState(false);
    const { Play } = mediaControlSvg;
    const {
        ArrowRightSimpleLine,
        ArrowLeftSimpleLine,
        ShareSquare,
        HeartLine,
        Heart,
    } = userInterfaceSvg;
    const router = useRouter();
    const dispatchCommon = useStore(state => state.dispatchCommon);
    const openModalAuth = useStore(state => state.openModalAuth);
    // eslint-disable-next-line no-underscore-dangle
    const userId = useStore(state => state.userState.userDetail._id);
    const userDetail = useStore(state => state.userState.userDetail);
    const { mutate: toggle } = useToggleFavourite();
    const videoEl = useRef<HTMLVideoElement>(null);

    const isMobile = useIsMD();

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const checkedRating = checkRating(packageDetail.rating);

    const toggleWishlist = () => {
        if (userId) {
            toggle(packageDetail.tourId);
        } else {
            openModalAuth();
        }
    };

    const isInWishlist =
        userDetail.favouriteTours?.includes(packageDetail.tourId) ?? false;

    // eslint-disable-next-line sonarjs/cognitive-complexity

    const onEndAction = () => {
        setVideoPlay(false);
    };

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const shouldRenderImage = !videoPlay || (isMobile && videoPlay);

    let hideControlsTimeout: ReturnType<typeof setTimeout> | null = null;

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const togglePlayPause = () => {
        if (videoEl.current) {
            if (isPlaying) {
                videoEl.current.pause();
            } else {
                videoEl.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const toggleMute = () => {
        if (videoEl.current) {
            videoEl.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const toggleFullscreen = () => {
        if (videoEl.current) {
            if (!isFullscreen) {
                if (videoEl.current.requestFullscreen) {
                    videoEl.current.requestFullscreen();
                }
            } else if (document.exitFullscreen) {
                document.exitFullscreen();
            }
            setIsFullscreen(!isFullscreen);
        }
    };

    const handleVideoPlay = () => setIsPlaying(true);
    const handleVideoPause = () => setIsPlaying(false);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const handleVolumeChange = () =>
        setIsMuted(videoEl.current?.muted ?? false);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        const video = videoEl.current;

        if (video) {
            video.addEventListener("play", handleVideoPlay);
            video.addEventListener("pause", handleVideoPause);
            video.addEventListener("volumechange", handleVolumeChange);
        }

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            if (video) {
                video.removeEventListener("play", handleVideoPlay);
                video.removeEventListener("pause", handleVideoPause);
                video.removeEventListener("volumechange", handleVolumeChange);
            }
            document.removeEventListener(
                "fullscreenchange",
                handleFullscreenChange
            );
        };
    }, []);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const startHideControlsTimer = () => {
        if (hideControlsTimeout !== null) {
            clearTimeout(hideControlsTimeout);
        }
        hideControlsTimeout = setTimeout(() => {
            setShowControls(false);
        }, 5000);
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const handleMouseMove = () => {
        setShowControls(true);
    };

    useEffect(() => {
        if (videoPlay && videoEl.current?.requestFullscreen) {
            videoEl.current?.requestFullscreen();
        }
    }, [videoPlay]);

    const truncatedTitle =
        packageDetail.tourTitle.length > 40
            ? `${packageDetail.tourTitle.substring(0, 40)}...`
            : packageDetail.tourTitle;

    return (
        <MDBContainer
            fluid
            className={styles.headBgColor}
            style={{ paddingBottom: "1px" }}
        >
            <div className={styles.breadcrumbContainer}>
                <button
                    type="button"
                    className={styles.backToShopButton}
                    onClick={() => {
                        // Mappa il tourType del packageDetail al valore della query
                        let tourTypeQuery: string | null = null;
                        if (packageDetail.tourType) {
                            if (
                                packageDetail.tourType ===
                                TourType.INTOTHEREGION
                            ) {
                                tourTypeQuery = "region";
                            } else if (
                                packageDetail.tourType === TourType.ACROSSITALY
                            ) {
                                tourTypeQuery = "italy";
                            } else if (
                                packageDetail.tourType === TourType.DOWNTHECITY
                            ) {
                                tourTypeQuery = "city";
                            }
                        }

                        router.push(
                            {
                                pathname: URLS.allTours,
                                query: {
                                    tourType: tourTypeQuery,
                                    destination: null,
                                },
                            },
                            URLS.allTours,
                            { shallow: true }
                        );
                    }}
                >
                    <ArrowLeftSimpleLine />
                    <span>Back to the shop</span>
                </button>
                <div className={styles.breadcrumb}>
                    <span className={styles.breadcrumbText}>Home</span>
                    <span className={styles.breadcrumbArrow}>
                        <ArrowRightSimpleLine />
                    </span>
                    <span className={styles.breadcrumbText}>Tours</span>
                    <span className={styles.breadcrumbArrow}>
                        <ArrowRightSimpleLine />
                    </span>
                    <span className={styles.breadcrumbText}>
                        {packageDetail.tourType}
                    </span>
                    <span className={styles.breadcrumbArrow}>
                        <ArrowRightSimpleLine />
                    </span>
                    <span className={styles.breadcrumbTextFinal}>
                        {truncatedTitle}
                    </span>
                </div>
            </div>
            <div
                className={styles.headBgImageContainer}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setShowControls(false)}
            >
                <div
                    style={{ display: videoPlay ? "block" : "flex" }}
                    className={styles.headPaddingContainer}
                >
                    {/* // IMPORTANTE: necessità di capire che tipo di video avremo */}
                    {/* // il codice sottostante garantise un video full-width senza bande nere */}
                    {/* // e centrato anche sul telefono, ma richiede l'url di un video e non il player youtube */}
                    {videoPlay && (
                        <div
                            style={{
                                width: isMobile ? "100%" : "90%",
                                height: "100%",
                                position: "relative",
                                top: "0px",
                                zIndex: isMobile ? 10 : 0,
                                objectFit: "cover",
                                borderRadius: "20px",
                                transform: isMobile ? "none" : "scale(1.5)",
                                transition: "transform 0.6s ease-in-out",
                                backgroundColor: "black",
                                marginBottom: isMobile ? "2rem" : "0",
                            }}
                        >
                            {/* // eslint-disable-next-line
                            sonarjs/cognitive-complexity */}
                            <video
                                className="w-100 h-100"
                                style={{
                                    objectFit: isMobile ? "contain" : "cover",
                                    paddingBottom: isMobile ? "1rem" : "0",
                                }}
                                controls
                                autoPlay
                                title="review video"
                                ref={videoEl}
                                onEnded={onEndAction}
                                muted={isMuted}
                                onPlay={startHideControlsTimer}
                                onPause={() => setShowControls(true)}
                            >
                                <source src={packageDetail.coverVideo} />
                            </video>
                            {!isMobile && (
                                <div
                                    className={`${styles.customControls} ${
                                        !showControls ? styles.hideControls : ""
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={toggleMute}
                                        className={styles.controlButton}
                                    >
                                        <Icon
                                            icon={
                                                isMuted
                                                    ? volumeMuteIcon
                                                    : volumeUpIcon
                                            }
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={togglePlayPause}
                                        className={styles.controlButton}
                                    >
                                        <Icon
                                            icon={
                                                isPlaying ? pauseIcon : playIcon
                                            }
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={toggleFullscreen}
                                        className={styles.controlButton}
                                    >
                                        <Icon
                                            icon={
                                                isFullscreen
                                                    ? compressIcon
                                                    : expandIcon
                                            }
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {shouldRenderImage && (
                        <div className={styles.imageContainer}>
                            <div className={styles.radialContainer} />
                            <Image
                                alt="tour-image"
                                src={
                                    error
                                        ? "/images/tours/across-italy-reduced.jpg"
                                        : packageDetail.coverImage
                                }
                                className={styles.imageFit}
                                fill
                                onError={() => {
                                    setError(true);
                                }}
                            />
                        </div>
                    )}
                    {!videoPlay && isVideoAvailable && (
                        <MDBBtn
                            className={`${styles.playButtonMobile} p-0 shadow-0`}
                            onClick={() => setVideoPlay(true)}
                        >
                            <Play />
                        </MDBBtn>
                    )}
                    <div className={styles.headTitleContainer}>
                        {isPromo && !videoPlay && (
                            <div
                                className={`rounded-5 ${styles.promoTag} text-center text-uppercase d-inline-flex px-3 py-2 my-2`}
                            >
                                Promo
                            </div>
                        )}
                        {!videoPlay && (
                            <>
                                {!isMobile && checkedRating && (
                                    <div className={styles.ratingContainerTop}>
                                        <div className={styles.starContainer}>
                                            {starsArray(
                                                packageDetail.rating,
                                                true
                                            )}
                                        </div>
                                        <span className={styles.starText}>
                                            {`${Number(packageDetail.rating)}`}
                                            {packageDetail.reviews &&
                                                packageDetail.reviews.length >
                                                    0 && (
                                                    <span
                                                        className={
                                                            styles.reviewsCount
                                                        }
                                                    >
                                                        {/* Questo mostra il numero di recensioni per quel tour, ad oggi, è richiesto di rimuoverlo */}
                                                        {/* {`(${packageDetail.reviews.length} reviews)`} */}
                                                    </span>
                                                )}
                                        </span>
                                        <span
                                            className={styles.shareButton}
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    window.location.href
                                                );
                                                dispatchCommon({
                                                    type: "SET_TOAST_MESSAGE_DATA",
                                                    payload: {
                                                        type: ETypeToastMessage.SUCCESS,
                                                        message: `Copied`,
                                                    },
                                                });
                                            }}
                                        >
                                            <ShareSquare />
                                        </span>
                                        <span
                                            className={styles.shareButton}
                                            onClick={toggleWishlist}
                                        >
                                            {isInWishlist ? (
                                                <Heart />
                                            ) : (
                                                <HeartLine />
                                            )}
                                        </span>
                                    </div>
                                )}
                                <h1 className={styles.headTitle}>
                                    {packageDetail.tourTitle}
                                </h1>
                                {isMobile && checkedRating && (
                                    <div className={styles.ratingContainerTop}>
                                        <div className={styles.starContainer}>
                                            {starsArray(
                                                packageDetail.rating,
                                                true
                                            )}
                                        </div>
                                        <span className={styles.starText}>
                                            {`${Number(packageDetail.rating)}`}
                                            {packageDetail.reviews &&
                                                packageDetail.reviews.length >
                                                    0 && (
                                                    <span
                                                        className={
                                                            styles.reviewsCount
                                                        }
                                                    >
                                                        {/* {`(${packageDetail.reviews.length} reviews)`} */}
                                                    </span>
                                                )}
                                        </span>
                                        <span
                                            className={styles.shareButton}
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    window.location.href
                                                );
                                                dispatchCommon({
                                                    type: "SET_TOAST_MESSAGE_DATA",
                                                    payload: {
                                                        type: ETypeToastMessage.SUCCESS,
                                                        message: `Copied`,
                                                    },
                                                });
                                            }}
                                        >
                                            <ShareSquare />
                                        </span>
                                        <span
                                            className={styles.shareButton}
                                            onClick={toggleWishlist}
                                        >
                                            {isInWishlist ? (
                                                <Heart />
                                            ) : (
                                                <HeartLine />
                                            )}
                                        </span>
                                    </div>
                                )}
                                {!isMobile &&
                                    !videoPlay &&
                                    isVideoAvailable && (
                                        <div
                                            className={
                                                styles.videoPreviewContainer
                                            }
                                            onClick={() => setVideoPlay(true)}
                                        >
                                            <div
                                                className={
                                                    styles.videoPreviewImage
                                                }
                                            >
                                                <Image
                                                    src={
                                                        error
                                                            ? "/images/tours/across-italy-reduced.jpg"
                                                            : packageDetail.whatWillYouSeeImage
                                                    }
                                                    alt="video-preview"
                                                    fill
                                                    className={
                                                        styles.videoPreviewImg
                                                    }
                                                    onError={() => {
                                                        setError(true);
                                                    }}
                                                />
                                                <div
                                                    className={
                                                        styles.videoPlayOverlay
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.videoPlayIcon
                                                        }
                                                    >
                                                        <Play />
                                                    </div>
                                                </div>
                                            </div>
                                            <p
                                                className={
                                                    styles.videoPreviewText
                                                }
                                            >
                                                Check out the video of{" "}
                                                <strong>
                                                    our amazing tour!
                                                </strong>
                                            </p>
                                        </div>
                                    )}
                            </>
                        )}
                    </div>
                    {!useIsMD() && !videoPlay && (
                        <HeadBox
                            days={days}
                            packageDetail={packageDetail}
                            isPremium={isPremium}
                            setIsPremium={setIsPremium}
                            typePackage={typePackage}
                            showAccomodations={showAccomodations}
                        />
                    )}
                </div>
                {useIsMD() && (
                    <HeadBox
                        isPremium={isPremium}
                        setIsPremium={setIsPremium}
                        isMobile
                        days={days}
                        packageDetail={packageDetail}
                        typePackage={typePackage}
                        showAccomodations={showAccomodations}
                    />
                )}
            </div>
        </MDBContainer>
    );
};

export default PackageDetailHead;
